// Tracking client : Meta Pixel (fbq) + GA4 (gtag). Le pixel et gtag sont
// initialisés dans index.html. Les helpers ici no-op silencieusement si
// le runtime n'est pas dispo (adblock, dev local sans tag, etc.).
//
// Architecture : client envoie PageView, Lead, InitiateCheckout, Purchase.
// Le serveur envoie les Purchase post-paiement réel (invoice.paid) via CAPI
// + GA4 Measurement Protocol — source de vérité, non bloquable par adblock.
// Pour les flows avec essai (Studio), Purchase est fire au J0 (CB saisie)
// avec une valeur réduite (proxy conversion), puis à J+15 avec la valeur
// pleine quand le 1er vrai prélèvement passe.

import { getAssignments } from './uiExperiments'

const PRICE_MAP = {
  basic:          { monthly: 39,  annual: 348 },
  createur:       { monthly: 79,  annual: 708 },
  pro:            { monthly: 249, annual: 2148 },
  studio_creator: { monthly: 39,  annual: 348 },
  // ABSENT depuis le lancement du plan (bug trouvé 2026-07-07) : 2/3 des
  // checkouts sont pro_plus et trackPurchase sortait en silence → AUCUN
  // Purchase pixel sur l'offre principale.
  studio_pro_plus: { monthly: 89,  annual: 828 },
}

// Trial conversion proxy values : pour les flows AVEC essai gratuit (Studio),
// on fire Purchase au J0 (CB saisie) mais avec une valeur faible — qui reflète
// le taux moyen de conversion trial→paid (~12%). Le vrai paiement J+15 envoie
// la valeur réelle. Permet à Meta d'optimiser sur "Purchase" sans gonfler
// artificiellement le ROAS apparent.
const TRIAL_VALUE_MAP = {
  studio_creator: { monthly: 5, annual: 30 },
  studio_pro_plus: { monthly: 10, annual: 100 },
}

// ─── Meta Pixel ────────────────────────────────────────────────
export function trackMetaEvent(eventName, params = {}) {
  if (typeof window === 'undefined' || !window.fbq) return
  try {
    window.fbq('track', eventName, params)
  } catch (e) {
    console.warn('[meta-pixel] tracking failed:', e)
  }
}

// ─── GA4 (gtag) ────────────────────────────────────────────────
export function trackGA4Event(eventName, params = {}) {
  if (typeof window === 'undefined' || !window.gtag) return
  try {
    window.gtag('event', eventName, params)
  } catch (e) {
    console.warn('[ga4] tracking failed:', e)
  }
}

// ─── Events combinés (Meta + GA4) ──────────────────────────────
export function trackLead({ method = 'email' } = {}) {
  trackMetaEvent('Lead', { content_name: 'signup', content_category: method })
  trackGA4Event('sign_up', { method })
}

export function trackInitiateCheckout({ plan, billingPeriod }) {
  const value = PRICE_MAP[plan]?.[billingPeriod]
  const params = value
    ? { value, currency: 'EUR', content_name: `${plan}_${billingPeriod}`, content_category: plan }
    : { content_name: plan }
  trackMetaEvent('InitiateCheckout', params)
  // AddPaymentInfo — fire en même temps que InitiateCheckout : l'user va
  // saisir sa CB sur Stripe Checkout dans la foulée. Signal fort de
  // qualification pour Meta (audience CB-savers).
  trackMetaEvent('AddPaymentInfo', params)
  trackGA4Event('begin_checkout', {
    currency: 'EUR',
    value: value || 0,
    items: [{ item_id: `${plan}_${billingPeriod}`, item_name: plan, item_category: 'subscription', price: value || 0, quantity: 1 }],
  })
  trackGA4Event('add_payment_info', {
    currency: 'EUR',
    value: value || 0,
    items: [{ item_id: `${plan}_${billingPeriod}`, item_name: plan, item_category: 'subscription', price: value || 0, quantity: 1 }],
  })
}

// ─── Studio Funnel events (GA4 + Supabase user_events) ─────────
// Chaque event est enrichi avec :
// - source_page : où l'user est (lp / signup / intake / studio_ready / studio_pro / admin)
// - utm_* : persistés depuis le 1er landing (localStorage)
// - variant : pour A/B testing (URL ?v=X ou random assigné)
// Préfixe studio_ pour grouper côté GA4.

const UTM_STORAGE_KEY = 'studio_utm_attribution'
const VARIANT_STORAGE_KEY = 'studio_ab_variant'
const ANON_ID_KEY = 'studio_anon_id'

// Cookie backup pour l'attribution. localStorage seul est fragile (Safari ITP
// clear après 7j sans interaction, in-app browsers volatiles). Le cookie sur
// .merlin-content-ai.com avec max-age 30j survit aux clear localStorage ET
// est partagé entre studio.*, www.*, et l'origin nu. Format : JSON URL-encodé
// avec les mêmes clefs que UTM_STORAGE_KEY.
const ATTRIBUTION_COOKIE = 'studio_attr'
const ATTRIBUTION_COOKIE_MAX_AGE = 30 * 24 * 3600 // 30 jours

function setAttributionCookie(value) {
  if (typeof document === 'undefined') return
  try {
    const encoded = encodeURIComponent(JSON.stringify(value || {}))
    // Domain leading-dot : partage cross-subdomain studio./www./root
    // Secure : seulement HTTPS (n'affecte pas localhost en dev)
    // SameSite=Lax : envoyé sur navigation top-level (suit le user click)
    const isLocal = window?.location?.hostname === 'localhost' || window?.location?.hostname?.startsWith('127.')
    const baseAttrs = `Max-Age=${ATTRIBUTION_COOKIE_MAX_AGE}; Path=/; SameSite=Lax`
    const cookieStr = isLocal
      ? `${ATTRIBUTION_COOKIE}=${encoded}; ${baseAttrs}`
      : `${ATTRIBUTION_COOKIE}=${encoded}; Domain=.merlin-content-ai.com; ${baseAttrs}; Secure`
    document.cookie = cookieStr
  } catch {}
}

function readAttributionCookie() {
  if (typeof document === 'undefined') return null
  try {
    const match = document.cookie.split(';').map(s => s.trim()).find(s => s.startsWith(`${ATTRIBUTION_COOKIE}=`))
    if (!match) return null
    const raw = decodeURIComponent(match.slice(ATTRIBUTION_COOKIE.length + 1))
    return JSON.parse(raw)
  } catch { return null }
}

// Backend URL pour l'endpoint anon (visible côté front via env). Sans ça
// les visiteurs pas encore loggés sont invisibles dans le funnel.
const API_URL_FOR_ANON = import.meta.env.VITE_API_URL || ''

// Génère / récupère un anon_id stable au scope navigateur. Persiste tant
// que l'user ne purge pas son localStorage. Une fois loggé on continue
// d'envoyer l'anon_id dans la metadata pour pouvoir JOIN ses events
// anon avec ses events post-signup et reconstruire le parcours complet.
function getAnonId() {
  if (typeof window === 'undefined') return null
  try {
    let id = localStorage.getItem(ANON_ID_KEY)
    if (!id) {
      id = `anon_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
      localStorage.setItem(ANON_ID_KEY, id)
    }
    return id
  } catch { return null }
}

// Indique si un user Supabase est actuellement loggé. On lit le tag posé
// par AuthContext (`studio_has_user`) ou à défaut la session Supabase v2.
// Évite un coupling direct avec le contexte React (utils doit rester pur).
function hasLoggedUser() {
  if (typeof window === 'undefined') return false
  try {
    if (window.__STUDIO_HAS_USER === true) return true
    // Fallback: Supabase JS v2 stocke 'sb-<ref>-auth-token' dans localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith('sb-') && k.endsWith('-auth-token')) {
        const v = localStorage.getItem(k)
        if (v && v.includes('access_token')) return true
      }
    }
  } catch {}
  return false
}

// Envoie un event anonyme directement au backend (insert user_events avec
// user_id=null, session_id=anon_id). Fire-and-forget, jamais bloquant.
function sendAnonEvent(eventType, metadata) {
  if (!API_URL_FOR_ANON) return
  const anonId = getAnonId()
  if (!anonId) return
  try {
    const payload = JSON.stringify({
      event_type: eventType,
      anon_id: anonId,
      page: typeof window !== 'undefined' ? window.location.pathname : null,
      metadata: metadata || {},
    })
    // sendBeacon prioritaire : survie même si user navigate away juste après
    // (cas lp_loaded → click sur signup → page change avant que fetch finish).
    // String brute (Content-Type text/plain, CORS-safelisted) et PAS un Blob
    // application/json : Chrome bloque les beacons cross-origin à type
    // non-safelisted (erreur preflight en console, vu au e2e du 2026-06-10).
    // Le backend parse le JSON du body quel que soit le Content-Type.
    if (navigator?.sendBeacon) {
      navigator.sendBeacon(`${API_URL_FOR_ANON}/api/track-anon-event`, payload)
      return
    }
    fetch(`${API_URL_FOR_ANON}/api/track-anon-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {})
  } catch {}
}

// Capture les UTM params au 1er landing et les persiste. Idempotent : si
// déjà en localStorage et qu'il n'y a pas de nouveaux UTM dans l'URL,
// garde la 1re attribution (last-click trap évité). Si nouveaux UTM dans
// l'URL, écrase (l'user a re-cliqué sur une nouvelle ad).
// fbp/fbc pour la CAPI : le cookie _fbp (posé par le pixel) + le fbclid du
// clic pub (persisté au format fbc). Sans eux, les conversions server-side
// ne matchent que par email → invisibles dans Ads Manager si l'email FB
// diffère de l'email d'inscription (incident 23/07 : 3 abos non attribués).
const FBC_STORAGE_KEY = 'merlin_fbc'
export function getMetaClickIds() {
  if (typeof window === 'undefined') return { fbp: null, fbc: null }
  try {
    const cookies = {}
    document.cookie.split(';').forEach(c => {
      const i = c.indexOf('=')
      if (i > 0) cookies[c.slice(0, i).trim()] = c.slice(i + 1).trim()
    })
    const fbp = cookies._fbp || null
    let fbc = cookies._fbc || null
    const fbclid = new URLSearchParams(window.location.search).get('fbclid')
    if (fbclid) {
      fbc = `fb.1.${Date.now()}.${fbclid}`
      try { localStorage.setItem(FBC_STORAGE_KEY, fbc) } catch {}
    }
    if (!fbc) { try { fbc = localStorage.getItem(FBC_STORAGE_KEY) || null } catch {} }
    return { fbp, fbc }
  } catch { return { fbp: null, fbc: null } }
}

export function captureUtmAndVariant() {
  if (typeof window === 'undefined') return
  try {
    // Persiste le fbclid dès le landing (l'URL SPA peut changer avant le checkout)
    getMetaClickIds()
    const params = new URLSearchParams(window.location.search)
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
    const newUtm = {}
    let hasNewUtm = false
    utmKeys.forEach(k => {
      const v = params.get(k)
      if (v) { newUtm[k] = v; hasNewUtm = true }
    })
    if (hasNewUtm) {
      newUtm.landed_at = new Date().toISOString()
      newUtm.landing_page = window.location.pathname
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(newUtm))
      // Backup cookie : survit aux clear localStorage (Safari ITP),
      // partagé cross-subdomain. Premier-write wins (on n'écrase pas
      // une attribution antérieure si l'user revient via une autre URL
      // sans UTM, on garde l'origine).
      if (!readAttributionCookie()) setAttributionCookie(newUtm)
    }
    // Variant A/B : depuis URL (?v=A) ou random assigné une fois
    const urlVariant = params.get('v') || params.get('variant')
    if (urlVariant) {
      localStorage.setItem(VARIANT_STORAGE_KEY, urlVariant)
    } else if (!localStorage.getItem(VARIANT_STORAGE_KEY)) {
      // Assignation aléatoire 50/50 si pas déjà fait
      localStorage.setItem(VARIANT_STORAGE_KEY, Math.random() < 0.5 ? 'A' : 'B')
    }
  } catch {}
}

function getStoredAttribution() {
  if (typeof window === 'undefined') return {}
  try {
    // 1. localStorage (path normal, rapide)
    let utm = {}
    try { utm = JSON.parse(localStorage.getItem(UTM_STORAGE_KEY) || '{}') } catch {}
    // 2. Fallback cookie : si localStorage vidé (Safari ITP) on récupère
    // l'attribution depuis le cookie .merlin-content-ai.com. On rehydrate
    // localStorage pour les events suivants.
    if (!utm || Object.keys(utm).length === 0) {
      const cookie = readAttributionCookie()
      if (cookie && Object.keys(cookie).length > 0) {
        utm = cookie
        try { localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(cookie)) } catch {}
      }
    }
    const variant = localStorage.getItem(VARIANT_STORAGE_KEY) || null
    return { ...utm, variant }
  } catch { return {} }
}

// Détecte device + viewport + OS + browser pour enrichir tous les events
// funnel. Permet de filtrer par mobile/desktop dans l'admin pour repérer
// les bugs spécifiques à une plateforme (ex: scan_failed à 80% mobile =
// bug mobile-specific). Cached au mount, viewport relu à chaque call.
const _DEVICE_INFO_CACHE = (() => {
  if (typeof window === 'undefined') return null
  try {
    const ua = navigator.userAgent || ''
    const uaLower = ua.toLowerCase()
    // Tablet d'abord (iPad / Android tablet), sinon mobile, sinon desktop.
    // iPadOS récent se déguise en Mac : on détecte aussi via maxTouchPoints.
    const isIPadOS = /macintosh/.test(uaLower) && (navigator.maxTouchPoints || 0) > 1
    let device_type = 'desktop'
    if (isIPadOS || /ipad|tablet|playbook|silk/.test(uaLower)) device_type = 'tablet'
    else if (/android/.test(uaLower) && !/mobile/.test(uaLower)) device_type = 'tablet'
    else if (/iphone|ipod|android.*mobile|windows phone|blackberry|webos|opera mini/.test(uaLower)) device_type = 'mobile'

    let os = 'unknown'
    if (/iphone|ipod/.test(uaLower)) os = 'ios'
    else if (isIPadOS || /ipad/.test(uaLower)) os = 'ipados'
    else if (/android/.test(uaLower)) os = 'android'
    else if (/windows/.test(uaLower)) os = 'windows'
    else if (/mac os|macintosh/.test(uaLower)) os = 'macos'
    else if (/linux/.test(uaLower)) os = 'linux'

    let browser = 'other'
    if (/edg\//.test(uaLower)) browser = 'edge'
    else if (/chrome\//.test(uaLower) && !/edg\/|opr\//.test(uaLower)) browser = 'chrome'
    else if (/firefox\//.test(uaLower)) browser = 'firefox'
    else if (/safari\//.test(uaLower) && !/chrome\//.test(uaLower)) browser = 'safari'
    else if (/opr\//.test(uaLower)) browser = 'opera'

    // Webview in-app Meta (Instagram/Facebook) : détecté globalement pour que
    // in_meta_webview soit sur CHAQUE event (avant, seuls note_wall/checkout le
    // posaient → signup_completed et scan_completed l'ignoraient, mesure webview
    // aveugle). Même regex que getWebviewInfo() dans StudioPage.
    const in_meta_webview = /Instagram|FBAN|FBAV|FB_IAB/i.test(ua)

    return { device_type, os, browser, in_meta_webview }
  } catch { return { device_type: 'unknown', os: 'unknown', browser: 'unknown', in_meta_webview: false } }
})()

function getDeviceInfo() {
  if (typeof window === 'undefined') return {}
  const base = _DEVICE_INFO_CACHE || {}
  let vw = 0, vh = 0, dpr = 1
  try {
    vw = window.innerWidth || document.documentElement?.clientWidth || 0
    vh = window.innerHeight || document.documentElement?.clientHeight || 0
    dpr = window.devicePixelRatio || 1
  } catch {}
  return {
    device_type: base.device_type,
    os: base.os,
    browser: base.browser,
    in_meta_webview: base.in_meta_webview,
    viewport_width: vw,
    viewport_height: vh,
    device_pixel_ratio: dpr,
  }
}

// Détecte la source_page courante depuis l'URL pour enrichir les events.
function detectSourcePage() {
  if (typeof window === 'undefined') return 'unknown'
  try {
    const search = new URLSearchParams(window.location.search)
    if (search.get('lp')) return 'lp'
    const path = window.location.pathname.toLowerCase()
    if (path === '/admin' || path.startsWith('/admin/')) return 'admin'
    if (path === '/cgu' || path === '/cgv' || path === '/mentions-legales' || path === '/confidentialite') return 'legal'
    // Studio default : la nuance ready/pro/signup/intake est dispatchée par le composant
    return 'studio'
  } catch { return 'unknown' }
}

function trackStudioFunnel(eventName, params = {}) {
  // Assignations d'expériences UI (A/B agents) : sur CHAQUE event, pour que
  // la mesure par variante soit automatique côté agents.
  // Import STATIQUE obligatoire : le require() dynamique d'origine plantait
  // silencieusement dans le bundle navigateur (pas de require en ESM/Vite)
  // → ui_exp n'a JAMAIS été attaché à un event avant le 24/07.
  let uiExp = null
  try { uiExp = getAssignments() } catch { /* noop */ }
  const fullEventName = `studio_${eventName}`
  const attribution = getStoredAttribution()
  const sourcePage = params.source_page || detectSourcePage()
  const device = getDeviceInfo()
  const anonId = getAnonId()
  // Verticale (?v=sport) : taguée sur CHAQUE event pour comparer les tunnels
  // vertical vs générique (LP → scan → signup → trial) sans instrumentation
  // supplémentaire. Le localStorage est posé par getVertical() (StudioPage).
  let vertical = null
  try { vertical = sessionStorage.getItem('studio_vertical') || localStorage.getItem('studio_vertical_persona') || null } catch { /* noop */ }
  const enrichedParams = {
    studio_step: eventName,
    timestamp_ms: Date.now(),
    source_page: sourcePage,
    anon_id: anonId,
    ...device,
    ...attribution,
    ...(uiExp && Object.keys(uiExp).length ? { ui_exp: uiExp } : {}),
    ...(vertical ? { vertical } : {}),
    ...params,
  }
  // GA4 (web analytics) — toujours fire, marche logged-in ou pas
  trackGA4Event(fullEventName, enrichedParams)
  if (typeof window !== 'undefined') {
    try {
      // ÉVÉNEMENTS CRITIQUES DE CHECKOUT (audit 21/07) : redirected part une
      // milliseconde avant window.location.href (la navigation annulait
      // l'insert Supabase → 25 « fuites » fantômes sur 30j) et failed survient
      // précisément quand la session est morte (l'insert RLS était rejeté →
      // 0 échec enregistré en 30j). Le beacon anonyme survit à la navigation
      // et ne dépend pas du JWT ; l'attribution passe par anon_id (metadata).
      if (eventName === 'checkout_redirected' || eventName === 'checkout_failed' || eventName === 'note_reveal_viewed') {
        // note_reveal_viewed part juste avant window.location.reload() du mur
        // de la note (bras A) : sans le beacon l'insert se perdait dans la
        // course au reload (0 révélation captée le 24-25/07 alors que des
        // comptes se créaient). Le beacon survit à la navigation, attribution
        // par anon_id → kill switch (complétion code) enfin mesurable.
        sendAnonEvent(fullEventName, enrichedParams)
      } else if (hasLoggedUser()) {
        // Path logged-in : CustomEvent capté par AuthContext qui écrit dans
        // user_events avec user_id du JWT Supabase
        window.dispatchEvent(new CustomEvent('studio:track-event', {
          detail: { eventType: fullEventName, metadata: enrichedParams },
        }))
      } else {
        // Path anonymous : POST direct au backend (user_id=null)
        // CRITIQUE pour le funnel : sans ça lp_loaded / signup_page_loaded
        // / intake_shown des visiteurs Meta Ads sont totalement invisibles
        sendAnonEvent(fullEventName, enrichedParams)
      }
    } catch {}
  }
}

export const studioEvents = {
  // ─── Funnel d'entrée (LP / signup) ─────────────────────────
  lpLoaded:              (p) => trackStudioFunnel('lp_loaded', { source_page: 'lp', ...p }),
  lpHeroCtaClicked:      (p) => trackStudioFunnel('lp_hero_cta_clicked', { source_page: 'lp', ...p }),
  lpPricingViewed:       (p) => trackStudioFunnel('lp_pricing_viewed', { source_page: 'lp', ...p }),
  lpSignupCtaClicked:    (p) => trackStudioFunnel('lp_signup_cta_clicked', { source_page: 'lp', ...p }),
  signupPageLoaded:      (p) => trackStudioFunnel('signup_page_loaded', { source_page: 'signup', ...p }),
  signupMethodChosen:    (p) => trackStudioFunnel('signup_method_chosen', { source_page: 'signup', ...p }), // google / email
  signupCompleted:       (p) => trackStudioFunnel('signup_completed', p),
  signupFailed:          (p) => trackStudioFunnel('signup_failed', { source_page: 'signup', ...p }),
  // Magic link en flux token_hash : page de confirmation 1-clic (anti-préchargement)
  magicConfirmViewed:    (p) => trackStudioFunnel('magic_confirm_viewed', { source_page: 'magic_confirm', ...p }),
  magicConfirmFailed:    (p) => trackStudioFunnel('magic_confirm_failed', { source_page: 'magic_confirm', ...p }),
  // Case d'engagement carte au paywall (réciprocité + commitment, 26/07)
  commitGateChecked:     (p) => trackStudioFunnel('commit_gate_checked', { source_page: 'paywall', ...p }),
  commitGateBlocked:     (p) => trackStudioFunnel('commit_gate_blocked', { source_page: 'paywall', ...p }),

  // ─── Intake (handle Instagram) ─────────────────────────────
  intakeShown:           (p) => trackStudioFunnel('intake_shown', { source_page: 'intake', ...p }),
  intakeHandleTyping:    (p) => trackStudioFunnel('intake_handle_typing', { source_page: 'intake', ...p }),
  gateHandleSubmitted:   (p) => trackStudioFunnel('gate_handle_submitted', { source_page: 'signup', ...p }), // @ soumis au gate (friction pré-réseau)
  checkoutRedirected:    (p) => trackStudioFunnel('checkout_redirected', p), // l'URL Stripe a été reçue, redirection lancée
  checkoutFailed:        (p) => trackStudioFunnel('checkout_failed', p),     // {error} — le backend/Stripe a refusé
  guestSessionClaimed:   (p) => trackStudioFunnel('guest_session_claimed', { source_page: 'signup', ...p }), // session invité rattachée au compte
  scanStarted:           (p) => trackStudioFunnel('scan_started', { source_page: 'intake', ...p }),
  // Pendant l'écran de chargement : combien de gens atteignent chaque palier
  // d'attente, et où ils décrochent. scanProgress = palier franchi (une fois
  // chacun) ; scanAbandoned = l'user quitte / met en arrière-plan avant la fin.
  scanProgress:          (p) => trackStudioFunnel('scan_progress', { source_page: 'loading', ...p }), // {milestone, pct, elapsed_ms, guest}
  scanAbandoned:         (p) => trackStudioFunnel('scan_abandoned', { source_page: 'loading', ...p }), // {pct, elapsed_ms, current_step, reason}
  scanCompleted:         (p) => trackStudioFunnel('scan_completed', p),
  scanFailed:            (p) => trackStudioFunnel('scan_failed', p), // include {reason, error_code}

  // ─── Generic mode (pas de compte Instagram) ───────────────
  // ─── Browse-first / discover mode (adset C) ──────────────
  discoverViewed:        (p) => trackStudioFunnel('discover_viewed', { source_page: 'discover', ...p }),
  discoverReelClicked:   (p) => trackStudioFunnel('discover_reel_clicked', { source_page: 'discover', ...p }), // {video_id, niche, format}
  discoverSignupCtaClicked: (p) => trackStudioFunnel('discover_signup_cta_clicked', { source_page: 'discover', ...p }),

  genericModeChosen:     (p) => trackStudioFunnel('generic_mode_chosen', { source_page: 'intake', ...p }),
  genericNicheSelected:  (p) => trackStudioFunnel('generic_niche_selected', { source_page: 'intake', ...p }), // {niche_macro, has_niche_fine}
  genericSessionCreated: (p) => trackStudioFunnel('generic_session_created', { source_page: 'intake', ...p }),
  genericModeDismissed:  (p) => trackStudioFunnel('generic_mode_dismissed', { source_page: 'intake', ...p }),

  // ─── Studio Ready (free post-scan) ────────────────────────
  scoreViewed:           (p) => trackStudioFunnel('score_viewed', p), // le radar /100 affiché (le wow)
  scoreImproveClicked:   (p) => trackStudioFunnel('score_improve_clicked', p), // CTA "Comment améliorer mon compte" — levier free→Pro+
  noteWallViewed:        (p) => trackStudioFunnel('note_wall_viewed', p),        // A/B mur de la note : écran de fin de scan affiché
  noteWallEmailFocus:    (p) => trackStudioFunnel('note_wall_email_focus', p),   // A/B mur de la note : focus champ email
  noteRevealViewed:      (p) => trackStudioFunnel('note_reveal_viewed', p),      // A/B mur de la note : révélation post-code vue
  vaFicheViewed:         (p) => trackStudioFunnel('va_fiche_viewed', p),         // fiche-cadeau analyse offerte affichée (mail free/past)
  vaFicheAnalyzeAnother: (p) => trackStudioFunnel('va_fiche_analyze_another_clicked', p), // CTA « Analyser une autre de mes vidéos »
  creatorDownsellShown:  (p) => trackStudioFunnel('creator_downsell_shown', p),  // clic volontaire « 89€ trop cher » → écran Créateur 39€
  unlockCtaClicked:      (p) => trackStudioFunnel('unlock_cta_clicked', p), // {trigger} — CTA de déblocage (scripts/formats) qui ouvre le paywall/signup
  formatsViewed:         (p) => trackStudioFunnel('formats_viewed', p),
  formatCardExpanded:    (p) => trackStudioFunnel('format_card_expanded', p),
  formatVideoPlayed:     (p) => trackStudioFunnel('format_video_played', p),
  formatFeedback:        (p) => trackStudioFunnel('format_feedback', p), // {format_id, airtable_id, format_nom, source_niche, verdict: adapted|not_adapted} — apprend le matching formats x niches
  tabClicked:            (p) => trackStudioFunnel('tab_clicked', p), // {tab: original/variables/trame, format_idx}
  tramePaywallShown:     (p) => trackStudioFunnel('trame_paywall_shown', p),
  webviewBannerShown:    (p) => trackStudioFunnel('webview_banner_shown', p), // {context, platform} — bannière "ouvre dans ton navigateur" affichée (webview Meta)
  webviewOpenClicked:    (p) => trackStudioFunnel('webview_open_clicked', p), // {context, method} — clic pour sortir de la webview
  noteHandoffStay:       (p) => trackStudioFunnel('note_handoff_stay', p), // {platform} — a choisi de rester dans le webview (voir la note ici) au lieu d'ouvrir le navigateur
  tramePaywallClicked:   (p) => trackStudioFunnel('trame_paywall_clicked', p),

  // ─── Popups poussant à l'upgrade ──────────────────────────
  popupLibraryShown:     (p) => trackStudioFunnel('popup_library_shown', p),
  popupLibraryClicked:   (p) => trackStudioFunnel('popup_library_clicked', p),
  popupLibraryDismissed: (p) => trackStudioFunnel('popup_library_dismissed', p),
  popupAnalyseShown:     (p) => trackStudioFunnel('popup_analyse_shown', p),
  popupAnalyseClicked:   (p) => trackStudioFunnel('popup_analyse_clicked', p),
  popupAnalyseDismissed: (p) => trackStudioFunnel('popup_analyse_dismissed', p),

  // ─── Onboarding d'activation (1er passage Studio Pro) ─────
  // Objectif métier : 100% des résiliés d'essai n'ont jamais activé (0 scan
  // /action). Ces events mesurent l'impact de la séquence de bienvenue +
  // checklist sur le passage à la 1ère action clé (ouvrir/sauver un format).
  onboardingShown:       (p) => trackStudioFunnel('onboarding_shown', { source_page: 'studio_pro', ...p }), // overlay affiché au 1er passage
  onboardingSlide:       (p) => trackStudioFunnel('onboarding_slide', { source_page: 'studio_pro', ...p }), // {index} — slide vu
  onboardingCompleted:   (p) => trackStudioFunnel('onboarding_completed', { source_page: 'studio_pro', ...p }), // dernier slide validé via CTA
  onboardingStep:        (p) => trackStudioFunnel('onboarding_step', { source_page: 'studio_pro', ...p }), // {step_index, step_title} — funnel du tuto (où ça décroche)
  onboardingDismissed:   (p) => trackStudioFunnel('onboarding_dismissed', { source_page: 'studio_pro', ...p }), // {index} — fermé avant la fin
  onboardingCtaOpenFormat:(p) => trackStudioFunnel('onboarding_cta_open_format', { source_page: 'studio_pro', ...p }), // CTA "Ouvre mon premier format"
  activationStep:        (p) => trackStudioFunnel('activation_step', { source_page: 'studio_pro', ...p }), // {step} — case cochée (score/open/save)

  // ─── Studio Pro ────────────────────────────────────────────
  studioProHomeShown:    (p) => trackStudioFunnel('studio_pro_home_shown', { source_page: 'studio_pro', ...p }),
  libraryVisited:        (p) => trackStudioFunnel('library_visited', p),
  weeklyDropRefresh:     (p) => trackStudioFunnel('weekly_drop_refresh', p),
  videoAnalysisStarted:  (p) => trackStudioFunnel('video_analysis_started', p),
  accountAnalysisStarted:(p) => trackStudioFunnel('account_analysis_started', p),
  formatSaved:           (p) => trackStudioFunnel('format_saved', p),
  formatCopied:          (p) => trackStudioFunnel('format_copied', p), // {what: script/template/trame}

  // ─── Upgrade modal + checkout ─────────────────────────────
  upgradeModalShown:     (p) => trackStudioFunnel('upgrade_modal_shown', p), // {trigger, upgrade_to}
  upgradeModalPeriodSwitched: (p) => trackStudioFunnel('upgrade_modal_period_switched', p), // {period: monthly/annual}
  upgradeModalPlanChosen:(p) => trackStudioFunnel('upgrade_modal_plan_chosen', p), // {plan, period, bypass_trial}
  upgradeModalClosed:    (p) => trackStudioFunnel('upgrade_modal_closed', p),
  checkoutStarted:       (p) => trackStudioFunnel('checkout_started', p),
  checkoutCompleted:     (p) => trackStudioFunnel('checkout_completed', p),
  checkoutAbandoned:     (p) => trackStudioFunnel('checkout_abandoned', p),
  trialStarted:          (p) => trackStudioFunnel('trial_started', p),

  pageViewed:            (p) => trackStudioFunnel('page_viewed', p),
}

// Purchase client-side. Si le plan a un trial (Studio), passe `isTrialStart=true`
// pour que la valeur envoyée à Meta soit la trial proxy value (5€/30€) au
// lieu du prix nominal. L'event Purchase reste un signal de conversion pour
// les ad campaigns sans gonfler artificiellement le ROAS apparent.
// Le vrai prélèvement J+15 envoie la valeur pleine côté serveur (webhook).
export function trackPurchase({ plan, billingPeriod, isTrialStart = false }) {
  const fullValue = PRICE_MAP[plan]?.[billingPeriod]
  if (!fullValue) return
  const trialValue = isTrialStart ? TRIAL_VALUE_MAP[plan]?.[billingPeriod] : null
  const value = trialValue ?? fullValue
  trackMetaEvent('Purchase', {
    value, currency: 'EUR',
    content_name: `${plan}_${billingPeriod}`,
    content_category: plan,
    ...(isTrialStart ? { predicted_ltv: fullValue } : {}),
  })
  trackGA4Event('purchase', {
    transaction_id: `${plan}_${billingPeriod}_${Date.now()}`,
    currency: 'EUR',
    value,
    items: [{ item_id: `${plan}_${billingPeriod}`, item_name: plan, item_category: 'subscription', price: value, quantity: 1 }],
  })
}
